/**
 * Branding store -- Zustand store for platform branding state.
 *
 * Holds the current branding config (primary/secondary colors, logo, platform name,
 * default theme). Seeded from Opus defaults, then overwritten by BrandingProvider
 * once the /branding endpoint responds.
 *
 * NOT persisted via Zustand persist (localStorage caching is handled by
 * applyBranding in brandTheme.ts for FOUC prevention).
 */

import { create } from 'zustand';
import { BRANDING_DEFAULTS, type BrandingPublicShape } from '@/config/brandingDefaults';

interface BrandingState {
  /** Current branding configuration */
  branding: BrandingPublicShape;
  /** Whether the initial fetch has completed */
  isLoaded: boolean;
  /** Set branding values (overwrites entire shape) */
  setBranding: (branding: BrandingPublicShape) => void;
  /** Partial update (merges with current) */
  updateBranding: (partial: Partial<BrandingPublicShape>) => void;
  /** Mark as loaded (fetch completed) */
  setLoaded: () => void;
}

export const useBrandingStore = create<BrandingState>()((set, get) => ({
  branding: BRANDING_DEFAULTS,
  isLoaded: false,

  setBranding: (branding: BrandingPublicShape) => {
    set({ branding });
  },

  updateBranding: (partial: Partial<BrandingPublicShape>) => {
    const current = get().branding;
    set({ branding: { ...current, ...partial } });
  },

  setLoaded: () => {
    set({ isLoaded: true });
  },
}));
